import { SecureLoader } from "@ews-admin/global-design-system";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSessionExpiration } from "../../hooks/useSessionExpiration";
import { validateSessionRequest } from "../../store/reducers/sessionSlice";
import { RootState } from "../../store/store";
import { storeQueryParams } from "../../utils/session-utils";
import i18n from "../../i18n";

const SUPPORTED_LANGUAGES = ["en", "fr"] as const;

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.session);

  // Monitor session expiration
  useSessionExpiration();

  useEffect(() => {
    // Read language BEFORE storeQueryParams removes all URL params
    const urlParams = new URLSearchParams(document.location.search);
    const urlLanguage = urlParams.get("language");
    const storedLanguage = localStorage.getItem("language");
    const language: string =
      (SUPPORTED_LANGUAGES as readonly string[]).includes(urlLanguage ?? "")
        ? urlLanguage!
        : (SUPPORTED_LANGUAGES as readonly string[]).includes(storedLanguage ?? "")
          ? storedLanguage!
          : "fr";

    storeQueryParams();

    i18n.changeLanguage(language);
    localStorage.setItem("language", language);

    dispatch(validateSessionRequest());
  }, [dispatch]);

  // Show loading while session is being validated
  if (loading) {
    return (
      <SecureLoader
        label={t("common.loading")}
        description={t("common.secureLoading")}
      />
    );
  }

  return <>{children}</>;
};
